import React, { Children, forwardRef, memo, useState } from "react";
import classNames from "classnames";
import { atomOneLight } from "react-syntax-highlighter/dist/cjs/styles/hljs";
import SyntaxHighlighter from "react-syntax-highlighter";
import TagWrapper from "./TagWrapper";
// import Icon from "../icon/Icon";
import Button from "./forms/Button";
import Icon from "./Icon";

export const CardLabel = forwardRef(
  (
    {
      tag = "div",
      className,
      children,
      icon,
      iconColor = "primary",
      pre,
      ...props
    },
    ref
  ) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-label", className)}
        {...props}
      >
        {pre}
        {icon && (
          <Icon
            iconName={icon}
            className={classNames("card-icon", {
              [`text-${iconColor}`]: iconColor,
            })}
            {...props}
          />
        )}
        <div className="card-title-wrapper">{children}</div>
      </TagWrapper>
    );
  }
);

export const CardActions = forwardRef(
  ({ tag = "div", className, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-actions", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardTitle = forwardRef(
  ({ tag = "h5", className, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-title", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardSubTitle = forwardRef(
  ({ tag = "h6", className, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-subtitle", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardHeader = forwardRef(
  (
    {
      tag = "div",
      className,
      children,
      size,
      borderSize,
      borderColor,
      ...props
    },
    ref
  ) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames(
          "card-header",
          {
            [`card-header-${size}`]: size,
            [`card-header-border-${borderSize}`]: borderSize,
            [`card-header-border-${borderColor}`]: borderColor,
          },
          className
        )}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardBody = forwardRef(
  ({ tag = "div", className, isScrollable, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames(
          "card-body",
          { "card-body-scrollable": isScrollable },
          className
        )}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardCodeView = memo(
  ({
    children,
    language = "jsx",
    customStyle,
    isPrismJs = true,
    className,
  }) => {
    // if (isPrismJs) {
    //   return (
    //     <PrismCode
    //       code={children}
    //       language={language}
    //       className={classNames("my-0", className)}
    //       style={customStyle}
    //     />
    //   );
    // }
    return (
      <SyntaxHighlighter
        language={language}
        style={atomOneLight}
        customStyle={{
          borderRadius: 13,
          backgroundColor: "var(--bs-light)",
          fontSize: "1rem",
          padding: "1.5rem 2rem",
          ...customStyle,
        }}
        wrapLongLines
        PreTag="code"
        className={classNames("shadow-sm", className)}
      >
        {children}
      </SyntaxHighlighter>
    );
  }
);

export const CardFooterLeft = forwardRef(
  ({ tag = "div", className, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-footer-left", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardFooterRight = forwardRef(
  ({ tag = "div", className, children, ...props }, ref) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames("card-footer-right", className)}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardFooter = forwardRef(
  (
    {
      tag = "div",
      className,
      children,
      size,
      borderSize,
      borderColor,
      ...props
    },
    ref
  ) => {
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames(
          "card-footer",
          {
            [`card-footer-${size}`]: size,
            [`card-footer-border-${borderSize}`]: borderSize,
            [`card-footer-border-${borderColor}`]: borderColor,
          },
          className
        )}
        {...props}
      >
        {children}
      </TagWrapper>
    );
  }
);

export const CardTabItem = ({ id, title, icon, children }) => {
  throw new Error(
    `Title ${title} component should be used as a child in the component Card.Id: ${id}, Icon Name: ${icon}, Children: ${children},`
  );
};

const Card = forwardRef(
  (
    {
      tag = "div",
      className,
      children,
      hasTab,
      tabButtonColor = "primary",
      tabBodyClassName,
      shadow,
      borderSize,
      borderColor,
      stretch,
      isCompact,
      ...props
    },
    ref
  ) => {
    const [activeTab, setActiveTab] = useState(0);
    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        className={classNames(
          "card",
          {
            [`card-stretch-${stretch === "semi" ? "semi" : "full"}`]: stretch,
            "card-compact": isCompact,
            [`shadow${shadow !== "md" ? `-${shadow}` : ""}`]:
              !!shadow && shadow !== "3d",
            [`u-shadow-3d--primary-sm`]: shadow === "3d",
            border: borderSize || borderSize === 0,
            [`border-${borderSize}`]: borderSize || borderSize === 0,
            [`border-${borderColor}`]: borderColor,
          },
          className
        )}
        {...props}
      >
        {!hasTab ? (
          children
        ) : (
          <>
            <CardHeader borderSize={1}>
              <CardActions>
                {Children.map(children, (item, index) => (
                  <Button
                    key={item.props.id}
                    isLight
                    color={index === activeTab ? tabButtonColor : "light"}
                    role="tab"
                    aria-controls={item.props.id}
                    aria-selected={index === activeTab}
                    isActive={index === activeTab}
                    icon={item.props.icon || null}
                    onClick={() => setActiveTab(index)}
                  >
                    {item.props.title}
                  </Button>
                ))}
              </CardActions>
            </CardHeader>
            {Children.map(children, (item, index) => {
              if (activeTab === index) {
                return (
                  <CardBody
                    key={item.props.id}
                    role="tabpanel"
                    aria-labelledby={item.props.id}
                    className={tabBodyClassName}
                  >
                    {item.props.children}
                  </CardBody>
                );
              }
              return null;
            })}
          </>
        )}
      </TagWrapper>
    );
  }
);

export default Card;
